import React, { createContext, useContext, ReactNode } from 'react';

import enTranslations from '../languages/en.json';
import frTranslations from '../languages/fr.json';
import arTranslations from '../languages/ar.json';
import esTranslations from '../languages/es.json';

type Language = 'en' | 'fr' | 'ar' | 'es'; // Ajoutez d'autres langues au besoin

const supportedLanguages: Language[] = ['en', 'fr', 'ar', 'es'];

const isLanguage = (value: string | null): value is Language =>
  value !== null && supportedLanguages.includes(value as Language);

const translations: Record<Language, Record<string, string>> = {
  en: enTranslations,
  fr: frTranslations,
  ar: arTranslations,
  es:esTranslations
};

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  translate: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = React.useState<Language>(() => {
    const storedLanguage = localStorage.getItem('language');
    return isLanguage(storedLanguage) ? storedLanguage : 'fr';
  });

  const setLang = (nextLang: Language) => {
    localStorage.setItem('language', nextLang);
    setLangState(nextLang);
  };

  const translate = (key: string) => translations[lang][key] || key;

  return (
    <LanguageContext.Provider value={{ lang, setLang, translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslate() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslate must be used within a LanguageProvider');
  }
  return context;
}
